import React, { useEffect, useMemo, useState } from "react";
import ModalShell from "./ModalShell";
import "./AssignedExpertsModal.css";

/* ===== Expert status i18n + normalization (локально в модалке) ===== */
const EXPERT_I18N = {
  kz: {
    headers: { name: 'Аты-жөні', subject: 'Пән', deadline: 'Дедлайн / Қалған уақыт', status: 'Күйі', action: 'Әрекет' },
    statuses: {
      accepted: 'Қабылдады',
      declined: 'Бас тартты',
      pending: 'Күту',
      invited: 'Шақырылды',
      in_review: 'Сараптамада',
      viewed: 'Қарады',
      draft: 'Жоба',
      sent: 'Жіберілді',
      unknown: 'Белгісіз',
    },
  },
  ru: {
    headers: { name: 'ФИО', subject: 'Предмет', deadline: 'Дедлайн / Осталось', status: 'Статус', action: 'Действие' },
    statuses: {
      accepted: 'Принял',
      declined: 'Отказался',
      pending: 'Ожидание',
      invited: 'Приглашён',
      in_review: 'На экспертизе',
      viewed: 'Просмотрел',
      draft: 'Черновик',
      sent: 'Отправлено',
      unknown: 'Неизвестно',
    },
  },
};

const EXPERT_STATUS_ALIASES = {
  accepted:   [/қабылдады/i, /принял[аио]?/i, /accepted/i],
  declined:   [/бас\s*тартты/i, /отказ|отклонил/i, /(declined|rejected)/i],
  pending:    [/күту|күтуде/i, /ожид/i, /pending/i],
  invited:    [/шақыр(ыл|ылды)/i, /приглаш[её]н/i, /invited/i],
  in_review:  [/сарапт|бағалау/i, /на\s+экспертиз/i, /review/i],
  viewed:     [/қарады/i, /просмотрел/i, /viewed/i],
  draft:      [/жоба/i, /черновик/i, /draft/i],
  sent:       [/жіберілді/i, /отправлен/i, /sent/i],
};

function expertStatusKeyFromRaw(raw) {
  const s = String(raw ?? '').normalize('NFKC').replace(/\s+/g, ' ').trim();
  for (const [key, regs] of Object.entries(EXPERT_STATUS_ALIASES)) {
    if (regs.some((re) => re.test(s))) return key;
  }
  return 'unknown';
}

function expertStatusClassByKey(key) {
  switch (key) {
    case 'accepted': return 'severity-success';
    case 'declined': return 'severity-danger';
    case 'pending': return 'severity-warning';
    case 'invited': return 'severity-info';
    case 'in_review': return 'severity-info';
    case 'viewed': return 'severity-neutral';
    case 'draft': return 'severity-neutral';
    case 'sent': return 'severity-info';
    default: return 'severity-neutral';
  }
}


function formatTimeLeft(minutes, locale = "kz") {
  if (minutes == null || isNaN(minutes)) return "—";
  const labels = locale === "ru"
    ? { d: "день", h: "ч", m: "мин", overdue: "Просрочено", closed: "Окно закрыто" }
    : { d: "күн", h: "сағ", m: "мин", overdue: "Мерзімі өтті", closed: "Терезе жабық" };
  if (minutes < 0) return labels.overdue;
  const d = Math.floor(minutes / 1440);
  const h = Math.floor((minutes % 1440) / 60);
  const m = Math.floor(minutes % 60);
  const parts = [];
  if (d) parts.push(`${d} ${labels.d}`);
  if (h) parts.push(`${h} ${labels.h}`);
  if (m || parts.length === 0) parts.push(`${m} ${labels.m}`);
  return parts.join(" ");
}

function getDeadlineSeverity(minutes) {
  if (minutes == null || isNaN(minutes)) return "neutral";
  if (minutes < 0) return "overdue";
  if (minutes <= 120) return "danger";
  if (minutes <= 1440) return "warning";
  return "success";
}

function getSubjectName(e, locale) {
  // 1) локализованные поля если есть
  const kz =
    e?.subject_name_kz ??
    e?.subject_kz ??
    e?.subject?.name_kz ??
    e?.subjectKz ??
    e?.subject_kaz ??
    (locale === 'kz' ? e?.subject_name : undefined);   // <- часто subject_name = KZ

  const ru =
    e?.subject_name_ru ??
    e?.subject_ru ??
    e?.subject?.name_ru ??
    e?.subjectRu ??
    (locale === 'ru' ? e?.subject_name : undefined);   // <- если ничего нет, хоть что-то покажем

  const val = locale === 'ru' ? (ru ?? kz ?? e?.subject ?? e?.subject_name) 
                              : (kz ?? ru ?? e?.subject ?? e?.subject_name);

  const s = val != null ? String(val).trim() : '';
  return s || '-';
}


export default function AssignedExpertsModal({
  t,
  locale = "kz",
  educationLevelId,
  experts = [],
  onSendMessage,
  onDeleteExpert,
  onConfirm,
  onClose,
}) {
  // Локальная копия — для мгновен
  // ного UI-обновления
   const L = useMemo(() => (EXPERT_I18N[locale] || EXPERT_I18N.kz), [locale]);
  const [rows, setRows] = useState(experts);
  useEffect(() => setRows(experts), [experts]);

  // Флаги запросов по id (чтобы дизейблить кнопки и не было дабл-кликов)
  const [sendingIds, setSendingIds] = useState({});
  const [deletingIds, setDeletingIds] = useState({});

  const [confirming, setConfirming] = useState(false);

const isPreschool = false; // или убери эту логику совсем если не используется


const accepted = useMemo(
  () => rows.filter((e) => expertStatusKeyFromRaw(e.status) === "accepted").length,
  [rows]
);
  const required = isPreschool ? 2 : 3;

const footer =
  accepted === required ? (
    <button
      className="gai-btn-primary"
      disabled={confirming}
      onClick={async () => {
        setConfirming(true);
        try {
          await onConfirm?.();
        } finally {
          setConfirming(false);
        }
      }}
    >
      {confirming
        ? (locale === 'kz' ? 'Бекітілуде...' : 'Подтверждение...')
        : (t?.confirm || (locale === 'kz' ? 'Бекіту' : 'Подтвердить'))}
    </button>
  ) : null;

    const colDeadlineTitle = L.headers.deadline;


  // ==== handlers ====
  const handleSendMessage = async (id) => {
    // Оптимистично обновляем строку
    setRows((prev) =>
      prev.map((e) =>
        e.id === id ? { ...e, status: "жіберілді", message_sent_at: new Date().toISOString() } : e
      )
    );
    setSendingIds((s) => ({ ...s, [id]: true }));

    try {
      const res = onSendMessage?.(id);
      if (res && typeof res.then === "function") await res; // если вернули промис — дождёмся
    } catch {
      // На ошибке можно откатить (опционально) — вернём "жоба"
      setRows((prev) =>
        prev.map((e) => (e.id === id ? { ...e, status: "жоба" } : e))
      );
    } finally {
      setSendingIds((s) => {
        const { [id]: _, ...rest } = s;
        return rest;
      });
    }
  };

  const handleDelete = async (id) => {
    // Оптимистично удаляем строку
    const backup = rows;
    setRows((prev) => prev.filter((e) => e.id !== id));
    setDeletingIds((s) => ({ ...s, [id]: true }));

    try {
      const res = onDeleteExpert?.(id);
      if (res && typeof res.then === "function") await res;
    } catch {
      // Откат, если ошибка
      setRows(backup);
    } finally {
      setDeletingIds((s) => {
        const { [id]: _, ...rest } = s;
        return rest;
      });
    }
  };

  return (
    <ModalShell title={t.viewAssigned} onClose={onClose} footer={footer}>
      <div className="gai-table-wrapper">
        <table className="gai-table">
          <thead>
  <tr>
    <th>{L.headers.name}</th>
    <th>{L.headers.subject}</th>
    <th>{L.headers.deadline}</th>
    <th>{L.headers.status}</th>
    <th>{L.headers.action}</th>
  </tr>
</thead>

          <tbody>
            {rows.map((expert) => {
              const dl = expert.accept_deadline ?? {};
const minutesLeft = Number(dl.minutes_left ?? expert.accept_minutes_left ?? 0);
const closed = (dl.open_count ?? expert.accept_deadlines_open) === 0;
const isOverdue = dl.is_overdue === true || minutesLeft < 0;

              const timeText = closed
                ? (locale === "ru" ? "Окно закрыто" : "Терезе жабық")
                : isOverdue
                ? (locale === "ru" ? "Просрочено" : "Мерзімі өтті")
                : formatTimeLeft(minutesLeft, locale);

              const severity = closed ? "closed" : getDeadlineSeverity(minutesLeft);

              const sending = !!sendingIds[expert.id];
              const deleting = !!deletingIds[expert.id];

              return (
                <tr key={expert.id}>
                  <td>{expert.name}</td>
                  <td>{getSubjectName(expert, locale)}</td>

                  {/* Дедлайн */}
                  <td
                    className={`gai-deadline chip severity-${severity}`}
                    title={dl.deadline_at ? `Дедлайн: ${dl.deadline_at}` : ""}
                  >
                    {timeText}
                  </td>

                  {/* Статус */}
                  <td>
  {(() => {
    const key = expertStatusKeyFromRaw(expert.status);
    const label = (t?.expertStatuses?.[key]) || L.statuses[key] || (expert.status || '—');
    return <span className={`chip ${expertStatusClassByKey(key)}`}>{label}</span>;
  })()}
</td>



                  {/* Действия */}
                  <td>
                    {expert.status === "жоба" ? (
                      <div className="gai-row-actions">
                        <button
  className="gai-btn-small"
  disabled={sending || deleting}
  onClick={() => handleSendMessage(expert.id)}
  title={sending ? (locale === 'ru' ? 'Отправляется…' : 'Жіберілуде...') : undefined}
>
  {sending ? "…" : t.sendMessage}
</button>

<button
  className="gai-btn-small gai-btn-danger"
  disabled={sending || deleting}
  onClick={() => handleDelete(expert.id)}
>
  {locale === "ru" ? "Удалить" : "Өшіру"}
</button>

                      </div>
                    ) : (
                      <span>—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </ModalShell>
  );
}
